import { h, toHtml, escapeHtml, type VNode } from "@broadpaper/core";
import { layoutDocument, type LayoutInput, type LayoutResult } from "./layout.js";
import { DomMeasurer } from "./measurer.js";
import { mountVNodes, ensureStyle } from "./dom.js";
import { DOCUMENT_CSS, fontFaceCss } from "./styles.js";

export interface PreviewOptions extends LayoutInput {
  /** Gap between pages in px. Default 24. */
  pageGap?: number;
  /** Scale factor. Default 1. */
  zoom?: number;
  /** Additional CSS class on the root. */
  className?: string;
}

export interface ReportPreview {
  /** Re-renders with new input (template, data, theme, ...). */
  update(input: Partial<PreviewOptions>): Promise<LayoutResult>;
  /** The last layout result. */
  result: LayoutResult | null;
  /** Scroll to a page (1-based). */
  goToPage(number: number): void;
  destroy(): void;
}

/** Waits for web fonts so measurement is stable. Resolves immediately when unsupported. */
export async function waitForFonts(doc: Document, families: string[] = []): Promise<void> {
  const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  try {
    await Promise.all(families.map((f) => fonts.load(`12px "${f}"`).catch(() => undefined)));
    await fonts.ready;
  } catch {
    /* ignore */
  }
}

/**
 * Mounts a live, paginated preview into a host element. This is the same
 * pipeline the editor canvas and the PDF service use.
 */
export function createReportPreview(container: HTMLElement, options: PreviewOptions): ReportPreview {
  const doc = container.ownerDocument;
  ensureStyle(doc, "bp-document-css", DOCUMENT_CSS);
  const measurer = new DomMeasurer(doc);
  let current: PreviewOptions = options;
  let destroyed = false;

  const root = doc.createElement("div");
  root.className = `bp-doc bp-preview ${options.className ?? ""}`.trim();
  container.appendChild(root);

  const api: ReportPreview = {
    result: null,
    async update(input) {
      current = { ...current, ...input };
      const theme = current.theme;
      ensureStyle(doc, "bp-theme-fonts", theme ? fontFaceCss({ ...theme, id: theme.id ?? "t", name: theme.name ?? "", typography: theme.typography ?? { headingFont: "", bodyFont: "" }, colors: theme.colors ?? ({} as never) }) : "");
      await waitForFonts(doc, (theme?.fonts ?? []).map((f) => f.family));
      if (destroyed) return api.result!;
      let result = layoutDocument(current, measurer);
      // Measured while an image was decoding or a face was loading, so shorter than
      // it draws: the pages would end above their content and the last rows of a
      // table would never be shown. Laid out again once it has arrived — a bounded
      // number of times, because a pass can only find what the one before it drew.
      for (let pass = 0; pass < 3 && (await measurer.settled()); pass++) {
        if (destroyed) return api.result ?? result;
        result = layoutDocument(current, measurer);
      }
      api.result = result;
      const gap = current.pageGap ?? 24;
      const zoom = current.zoom ?? 1;
      root.classList.toggle("bp-doc--design", result.mode === "design");
      const pages = result.renderPages().map((p, i) => h("div", { class: "bp-preview__page", style: { "margin-bottom": i < result.paged.totalPages - 1 ? `${gap}px` : "0", "box-shadow": "0 1px 3px rgba(16,24,40,0.12), 0 8px 24px rgba(16,24,40,0.08)" } }, p));
      const inner: VNode = h("div", { class: "bp-preview__pages", style: { transform: zoom !== 1 ? `scale(${zoom})` : undefined, "transform-origin": "top center", width: `${result.page.width}px`, margin: "0 auto" } }, pages);
      mountVNodes(root, [inner]);
      return result;
    },
    goToPage(number) {
      const el = root.querySelector<HTMLElement>(`[data-page="${number}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    destroy() {
      destroyed = true;
      measurer.destroy();
      root.remove();
    }
  };
  void api.update({});
  return api;
}

export interface HtmlDocumentOptions {
  title?: string;
  /** Extra CSS appended after the document stylesheet. */
  extraCss?: string;
  /** Include the design-mode class (rarely wanted for PDFs). */
  design?: boolean;
  /** Document language for the html element. */
  lang?: string;
  /** PDF metadata. */
  author?: string;
  subject?: string;
  keywords?: string[];
}

/**
 * Serialises a laid-out document to a standalone HTML page: one fixed-size box
 * per page with `@page` sized to match. This is what the PDF service prints.
 */
export function documentToHtml(result: LayoutResult, opts: HtmlDocumentOptions = {}): string {
  const pages = result.renderPages();
  const { width, height } = result.page;
  const fonts = fontFaceCss(result.theme);
  const css = `${fonts}\n${DOCUMENT_CSS}\n@page{size:${width}px ${height}px;margin:0;}\nhtml,body{margin:0;padding:0;background:#fff;}\n.bp-page{break-after:page;page-break-after:always;}\n.bp-page:last-child{break-after:auto;page-break-after:auto;}\n${opts.extraCss ?? ""}`;
  const meta = [
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=${width}">`,
    `<meta name="generator" content="BroadPaper">`,
    opts.author ? `<meta name="author" content="${escapeHtml(opts.author)}">` : "",
    opts.subject ? `<meta name="description" content="${escapeHtml(opts.subject)}">` : "",
    opts.keywords?.length ? `<meta name="keywords" content="${escapeHtml(opts.keywords.join(", "))}">` : ""
  ].join("\n");
  const body = pages.map((p) => toHtml(p)).join("\n");
  return `<!doctype html>\n<html lang="${escapeHtml(opts.lang ?? "en")}">\n<head>\n${meta}\n<title>${escapeHtml(opts.title ?? "Report")}</title>\n<style>${css}</style>\n</head>\n<body class="bp-doc${opts.design ? " bp-doc--design" : ""}" data-pages="${result.paged.totalPages}">\n${body}\n</body>\n</html>`;
}
